<template>
  <div class="ncrm-lead-details-page">
    <NcrmRecordLayout :left-lg-cols="3" :main-lg-cols-both="7" :right-lg-cols="2">
    <template #header>
      <NcrmRecordHeader
        :label="$t('ncrm.leads.details.recordLabel')"
        :title="recordTitle"
        :subtitle="recordSubtitle"
        :chips="headerChips"
        show-back-button
        :back-aria-label="$t('app.actions.back')"
        @back-click="goBackToLeads"
      >
        <template #kpis>
          <div class="ncrm-record-header__kpis">
            <v-menu v-if="quickOwnerOptions.length" v-model="menus.owner" location="bottom start" :close-on-content-click="true">
              <template #activator="{ props: menuProps }">
                <button
                  v-bind="menuProps"
                  type="button"
                  class="ncrm-record-header__kpi ncrm-record-header__kpi--clickable"
                  :disabled="quickActionLoading"
                  :aria-label="`${$t('ncrm.leads.details.fields.owner')}: ${ownerLabel(record)}`"
                >
                  <div class="d-flex align-center ga-1 text-caption text-medium-emphasis">
                    <v-icon size="14">mdi-account</v-icon>
                    <span>{{ $t("ncrm.leads.details.fields.owner") }}</span>
                  </div>
                  <div class="d-flex align-center justify-space-between ga-2">
                    <div class="text-body-2 ncrm-record-header__kpi-value">
                      {{ ownerLabel(record) }}
                    </div>
                    <v-icon size="16">mdi-menu-down</v-icon>
                  </div>
                </button>
              </template>
              <v-list density="compact" class="ncrm-lead-details__kpi-menu-list">
                <v-list-item
                  v-for="option in quickOwnerOptions"
                  :key="`lead-owner-option-${option.id}`"
                  :title="option.label"
                  :active="option.id === currentOwnerUserId"
                  :disabled="quickActionLoading"
                  @click="applyOwnerInline(option.id)"
                />
              </v-list>
            </v-menu>
            <div v-else class="ncrm-record-header__kpi">
              <div class="d-flex align-center ga-1 text-caption text-medium-emphasis">
                <v-icon size="14">mdi-account</v-icon>
                <span>{{ $t("ncrm.leads.details.fields.owner") }}</span>
              </div>
              <div class="text-body-2 ncrm-record-header__kpi-value">
                {{ ownerLabel(record) }}
              </div>
            </div>
            <button
              type="button"
              class="ncrm-record-header__kpi ncrm-record-header__kpi--clickable"
              :disabled="quickActionLoading || !canShowActivityTimeline"
              :aria-label="`${$t('ncrm.leads.details.fields.nextFollowup')}: ${formatDateTime(resolvedNextFollowupAt)}`"
              @click="openNextFollowupComposer"
            >
              <div class="d-flex align-center ga-1 text-caption text-medium-emphasis">
                <v-icon size="14">mdi-calendar-clock</v-icon>
                <span>{{ $t("ncrm.leads.details.fields.nextFollowup") }}</span>
              </div>
              <div class="d-flex align-center justify-space-between ga-2">
                <div class="text-body-2 ncrm-record-header__kpi-value">
                  {{ formatDateTime(resolvedNextFollowupAt) }}
                </div>
                <v-icon size="16">mdi-plus</v-icon>
              </div>
            </button>
            <v-menu v-if="quickStageOptions.length" v-model="menus.stage" location="bottom start" :close-on-content-click="true">
              <template #activator="{ props: menuProps }">
                <button
                  v-bind="menuProps"
                  type="button"
                  class="ncrm-record-header__kpi ncrm-record-header__kpi--clickable"
                  :disabled="quickActionLoading"
                  :aria-label="`${$t('ncrm.leads.details.actions.changeStage')}: ${currentStageLabel}`"
                >
                  <div class="d-flex align-center ga-1 text-caption text-medium-emphasis">
                    <v-icon size="14">mdi-flag-outline</v-icon>
                    <span>{{ $t("ncrm.leads.details.actions.changeStage") }}</span>
                  </div>
                  <div class="d-flex align-center justify-space-between ga-2">
                    <div class="text-body-2 ncrm-record-header__kpi-value">
                      {{ currentStageLabel }}
                    </div>
                    <v-icon size="16">mdi-menu-down</v-icon>
                  </div>
                </button>
              </template>
              <v-list density="compact" class="ncrm-lead-details__kpi-menu-list">
                <v-list-item
                  v-for="option in quickStageOptions"
                  :key="`lead-stage-option-${option.id}`"
                  :title="option.label"
                  :active="option.id === currentStageId"
                  :disabled="quickActionLoading"
                  @click="applyStageInline(option.id)"
                />
              </v-list>
            </v-menu>
            <div v-else class="ncrm-record-header__kpi">
              <div class="d-flex align-center ga-1 text-caption text-medium-emphasis">
                <v-icon size="14">mdi-flag-outline</v-icon>
                <span>{{ $t("ncrm.leads.details.actions.changeStage") }}</span>
              </div>
              <div class="text-body-2 ncrm-record-header__kpi-value">
                {{ currentStageLabel }}
              </div>
            </div>
            <button
              type="button"
              class="ncrm-record-header__kpi ncrm-record-header__kpi--clickable"
              :disabled="quickActionLoading || !canConvertLead"
              :aria-label="`${$t('ncrm.leads.details.actions.convertToDeal')}: ${currentStatusLabel}`"
              @click="openConvertDialog"
            >
              <div class="d-flex align-center ga-1 text-caption text-medium-emphasis">
                <v-icon size="14">mdi-swap-horizontal-bold</v-icon>
                <span>{{ $t("ncrm.leads.details.actions.convertToDeal") }}</span>
              </div>
              <div class="text-body-2 ncrm-record-header__kpi-value">
                {{ currentStatusLabel }}
              </div>
            </button>
          </div>
        </template>
        <template #actions>
          <NcrmLeadHeaderActions
            :lead-id="leadId"
            :disabled="!record"
            :loading="loading"
            @error="showSnackbar($event, 'error')"
            @refresh="fetchRecord"
          />
        </template>
      </NcrmRecordHeader>
    </template>
    <template #left>
      <NcrmRecordPropertiesCard
        :title="sourcePropertiesTitle"
        :loading="loading"
        :sections="sourcePropertySectionsForDisplay"
        :skeleton-groups="propertySkeletonGroups"
        :empty-label="emptyValueLabel"
        :empty-sections-label="$t('ncrm.contacts.details.propertySections.empty')"
        :copy-aria-label="$t('ncrm.contacts.details.propertyInteractions.copy')"
        :resolve-row-link="resolveSourcePropertyRowLink"
        :privacy-subject="{ type: sourceEntityType, id: sourceEntityId }"
        :resolve-row-tooltip="resolveSourceTimestampTooltip"
        :can-copy-row="canCopySourcePropertyRow"
        show-settings-button
        :settings-disabled="!sourcePropertyLayoutEndpoint || !record"
        :settings-aria-label="$t('ncrm.contacts.details.propertyLayout.actions.open')"
        @settings-click="openPropertyLayoutDrawer"
        @copy-row="copySourcePropertyRowValue"
      />
    </template>
    <template #main>
      <v-card class="pa-4">
        <div class="text-subtitle-1 ncrm-record-title">
          {{ $t("ncrm.leads.details.sections.mainWorkspace") }}
        </div>
        <v-progress-linear v-if="loading" indeterminate color="primary" class="mt-4" />
        <div v-else class="mt-3">
          <v-tabs v-model="mainTab" color="primary" density="comfortable" show-arrows class="ncrm-main-workspace-tabs">
            <v-tab value="activity">{{ $t("ncrm.leads.details.tabs.activity") }}</v-tab>
            <v-tab value="overview">{{ $t("ncrm.leads.details.tabs.overview") }}</v-tab>
            <v-tab value="mailjet">{{ $t("ncrm.integrations.mailjet.activity.tab") }}</v-tab>
            <v-tab value="history">{{ $t("ncrm.leads.details.tabs.history") }}</v-tab>
          </v-tabs>
          <v-window v-model="mainTab" class="ncrm-main-workspace-window" :touch="false">
            <v-window-item value="activity">
              <NcrmActivityTimeline
                v-if="mainTab === 'activity' && canShowActivityTimeline"
                ref="leadActivityTimeline"
                :entity-type="activityEntityType"
                :entity-id="activityEntityId"
                origin-entity-type="lead_entry"
                :origin-entity-id="leadId"
                scope="customer" sort-mode="active_overdue_first"
                @loaded="onLeadActivitiesLoaded"
                @created="onLeadActivityCreated"
                @update:composer-open="onLeadActivityComposerToggle"
              />
              <v-alert v-else type="info">
                {{ $t("ncrm.leads.details.activity.unavailable") }}
              </v-alert>
            </v-window-item>
            <v-window-item value="overview">
              <div v-if="overviewRows.length" class="ncrm-details-list mt-1">
                <div
                  v-for="row in overviewRows"
                  :key="`lead-overview-row-${row.key}`"
                  class="ncrm-details-list__item"
                >
                  <div class="text-caption text-medium-emphasis">{{ row.label }}</div>
                  <div class="text-body-2 ncrm-details-list__value">{{ row.value || emptyValueLabel }}</div>
                </div>
              </div>
              <v-alert v-else type="info">
                {{ $t("ncrm.leads.details.overview.empty") }}
              </v-alert>
            </v-window-item>
            <v-window-item value="mailjet">
              <NcrmMailjetActivityPanel
                v-if="mainTab === 'mailjet' && leadId"
                entity-type="lead"
                :entity-id="leadId"
                @notify="onMailjetNotify"
              />
            </v-window-item>
            <v-window-item value="history">
              <v-progress-linear v-if="historyLoading" indeterminate color="primary" class="mb-3" />
              <div v-else>
                <div
                  v-if="historyItems.length"
                  class="d-flex align-center justify-space-between ga-2 mb-2 ncrm-lead-details__history-toolbar"
                >
                  <div class="ncrm-lead-details__history-filter-wrap">
                    <div class="ncrm-lead-details__history-filter-label text-caption text-medium-emphasis">
                      {{ $t("ncrm.leads.details.history.filters.label") }}
                    </div>
                    <v-select
                      v-model="historyEventFilter"
                      :items="historyEventFilterItems"
                      item-title="label"
                      item-value="value"
                      hide-details
                      class="ncrm-lead-details__history-filter"
                    />
                  </div>
                </div>
                <v-list v-if="groupedHistoryItems.length" class="bg-transparent py-0 ncrm-lead-details__history-list">
                  <template v-for="(group, groupIndex) in groupedHistoryItems" :key="`lead-history-group-${group.key}`">
                    <v-list-subheader class="px-0 text-uppercase text-caption text-medium-emphasis">
                      {{ group.label }}
                    </v-list-subheader>
                    <template v-for="(historyItem, index) in group.items" :key="`lead-history-${historyItem.id || index}`">
                      <v-list-item class="px-0">
                        <v-list-item-title class="text-body-2 ncrm-text-strong d-flex align-center ga-2 flex-wrap">
                          <v-chip size="x-small" :color="historyEventColor(historyItem.event_type)">
                            {{ historyEventLabel(historyItem.event_type) }}
                          </v-chip>
                        </v-list-item-title>
                        <v-list-item-subtitle class="text-caption text-medium-emphasis mt-1">
                          {{ $t("ncrm.leads.details.history.changedByAt", {
                            user: historyChangedByLabel(historyItem),
                            date: formatDateTime(historyItem.created_at),
                          }) }}
                        </v-list-item-subtitle>
                        <v-list-item-subtitle v-if="hasHistoryValueChange(historyItem)" class="text-body-2 mt-2">
                          <span class="text-medium-emphasis">
                            {{ $t("ncrm.leads.details.history.from") }}:
                          </span>
                          <span>{{ historyItem.from_value || emptyValueLabel }}</span>
                          <span class="mx-2 text-medium-emphasis">→</span>
                          <span class="text-medium-emphasis">
                            {{ $t("ncrm.leads.details.history.to") }}:
                          </span>
                          <span>{{ historyItem.to_value || emptyValueLabel }}</span>
                        </v-list-item-subtitle>
                      </v-list-item>
                      <v-divider v-if="index < group.items.length - 1" />
                    </template>
                    <v-divider v-if="groupIndex < groupedHistoryItems.length - 1" class="my-2" />
                  </template>
                </v-list>
                <v-alert v-else type="info">
                  {{ $t("ncrm.leads.details.history.empty") }}
                </v-alert>
              </div>
            </v-window-item>
          </v-window>
        </div>
      </v-card>
    </template>
    <template #right>
      <v-card class="pa-4">
        <div class="text-subtitle-1 ncrm-record-title">
          {{ $t("ncrm.leads.details.sections.context") }}
        </div>
        <v-progress-linear v-if="loading" indeterminate color="primary" class="mt-4" />
        <div v-else class="mt-3">
          <div class="text-caption text-medium-emphasis">
            {{ $t("ncrm.leads.details.sections.sourceNavigation") }}
          </div>
          <NcrmLeadContextActions
            :board-id="board?.id"
            :source-route="sourceRoute"
            @open-board="openBoard"
            @open-source="openSourceRecord"
          />
          <div class="text-caption text-medium-emphasis">
            {{ $t("ncrm.leads.details.context.board") }}
          </div>
          <div class="text-body-2 ncrm-details-list__value mt-1">
            {{ board?.name || emptyValueLabel }}
          </div>
          <div class="text-caption text-medium-emphasis mt-3">
            {{ $t("ncrm.leads.details.context.sourceRecord") }}
          </div>
          <div class="text-body-2 ncrm-details-list__value mt-1">
            {{ sourceRecordLabel }}
          </div>
          <div class="text-caption text-medium-emphasis mt-3">
            {{ $t("ncrm.leads.details.context.nextBestAction") }}
          </div>
          <div
            class="mt-1 ncrm-lead-details__next-action-alert"
            :class="`ncrm-lead-details__next-action-alert--${nextBestAction.type || 'info'}`"
          >
            <v-icon class="ncrm-lead-details__next-action-icon" size="18">
              {{ nextBestActionIcon }}
            </v-icon>
            <span class="text-body-2">{{ nextBestAction.message }}</span>
          </div>
        </div>
      </v-card>
    </template>
    </NcrmRecordLayout>
    <v-snackbar v-model="snackbar.show" :timeout="3200" :color="snackbar.color">
      {{ snackbar.message }}
    </v-snackbar>
    <NcrmPropertyLayoutDrawer
      v-model="propertyLayoutDrawer"
      :title="$t('ncrm.contacts.details.propertyLayout.title')"
      :subtitle="$t('ncrm.contacts.details.propertyLayout.subtitle')"
      :items="sourcePropertyLayoutItems"
      :visible-keys="propertyLayoutVisibleKeys"
      :loading="propertyLayoutLoading"
      :saving="propertyLayoutSaving"
      @save="savePropertyLayout"
    />
    <NcrmLeadConvertDialog
      v-model="dialogs.convert"
      :title="$t('ncrm.leads.details.dialogs.convert.title')"
      :loading="quickActionLoading"
      :save-disabled="!canSaveConvertAction"
      :save-label="$t('ncrm.leads.details.actions.convertToDeal')"
      :deal-name="quickActions.dealName"
      :deal-pipeline-id="quickActions.dealPipelineId"
      :deal-stage-id="quickActions.dealStageId"
      :lead-stage-id="quickActions.convertStageId"
      :owner-user-id="quickActions.convertOwnerUserId"
      :note="quickActions.convertNote"
      :pipeline-options="quickDealPipelineOptions"
      :deal-stage-options="quickDealStageOptions"
      :lead-stage-options="quickConvertStageOptions"
      :owner-options="quickOwnerOptions"
      :select-menu-props="selectMenuProps"
      @update:deal-name="quickActions.dealName = $event"
      @update:deal-pipeline-id="quickActions.dealPipelineId = $event"
      @update:deal-stage-id="quickActions.dealStageId = $event"
      @update:lead-stage-id="quickActions.convertStageId = $event"
      @update:owner-user-id="quickActions.convertOwnerUserId = $event"
      @update:note="quickActions.convertNote = $event"
      @pipeline-change="onConvertPipelineChanged"
      @save="saveConvertAction"
    />
    <NcrmOwnerClaimDialog
      :model-value="ownerClaimDialog.open"
      :loading="ownerClaimDialog.loading"
      :title="$t('ncrm.leads.ownerClaimDialog.title')"
      :subtitle="$t('ncrm.leads.ownerClaimDialog.subtitle')"
      :message="$t('ncrm.leads.ownerClaimDialog.message')"
      :record-field-label="$t('ncrm.leads.ownerClaimDialog.fields.record')"
      :entity-name="ownerClaimDialog.entryName"
      :owner-field-label="$t('ncrm.leads.ownerClaimDialog.fields.currentOwner')"
      :current-owner-label="ownerClaimDialog.currentOwnerLabel"
      :empty-value-label="emptyValueLabel"
      :save-label="$t('ncrm.leads.ownerClaimDialog.actions.confirm')"
      @update:model-value="onOwnerClaimDialogModelUpdate"
      @confirm="confirmOwnerClaim"
    />
  </div>
</template>
<script>
import axios from "axios";
import NcrmRecordHeader from "@/components/ncrm/shared/NcrmRecordHeader.vue";
import NcrmRecordLayout from "@/components/ncrm/shared/NcrmRecordLayout.vue";
import NcrmActivityTimeline from "@/components/ncrm/shared/NcrmActivityTimeline.vue";
import NcrmRecordPropertiesCard from "@/components/ncrm/shared/NcrmRecordPropertiesCard.vue";
import NcrmPropertyLayoutDrawer from "@/components/ncrm/shared/NcrmPropertyLayoutDrawer.vue";
import NcrmOwnerClaimDialog from "@/components/ncrm/shared/NcrmOwnerClaimDialog.vue";
import NcrmLeadConvertDialog from "@/components/ncrm/leads/NcrmLeadConvertDialog.vue";
import NcrmLeadContextActions from "@/components/ncrm/leads/NcrmLeadContextActions.vue";
import NcrmLeadHeaderActions from "@/components/ncrm/leads/NcrmLeadHeaderActions.vue";
import NcrmMailjetActivityPanel from "@/components/ncrm/integrations/mailjet/NcrmMailjetActivityPanel.vue";
import { formatNcrmTimestampTooltip, resolveNcrmPropertyLinkMeta } from "@/Utile/ncrmRecordDetails.mjs";
import { isNcrmActivityOpenStatus, normalizeNcrmActivityList, normalizeNcrmActivityType } from "@/Utile/ncrmActivity.mjs";
import { resolveCurrentUserId } from "@/Utile/ncrmCurrentUser.mjs";
import { isAxiosRequestAborted } from "@/Utile/httpErrors.mjs";
import {
  buildNcrmPropertyLayoutEndpoint,
  buildNcrmPropertyRowMap,
  dedupeNcrmPropertyLayoutKeys,
  resolveNcrmOrderedPropertyRows,
  resolveNcrmPropertyLayoutItems,
  resolveNcrmPropertyLayoutState,
  sanitizeNcrmPropertyLayoutVisibleKeys,
} from "@/Utile/ncrmPropertyLayouts.mjs";
import {
  formatNcrmLeadDateTime,
  normalizeNcrmLeadEntryEntityType,
  normalizeNcrmLeadEntryPriority,
  normalizeNcrmLeadEntryStatus,
  normalizeNcrmLeadString,
  resolveNcrmLeadEntryDisplayName,
  resolveNcrmLeadEntryPriorityColor,
  resolveNcrmLeadEntrySourceRoute,
} from "@/Utile/ncrmLeads.mjs";
import { resolveLeadTerminalTaskClosureConfirmation } from "@/Utile/ncrmTerminalTaskClosure.mjs";
import { formatDisplayDate } from "@/Utile/displayFormat.mjs";
const parsePositiveId = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return null;
  const normalized = Math.trunc(parsed);
  return normalized > 0 ? normalized : null;
};
const LEAD_DETAILS_TABS = Object.freeze(["activity", "overview", "history", "mailjet"]);
const LEAD_PROPERTY_SECTION_ORDER = Object.freeze(["contact", "address", "business", "customFields", "system"]);
const PROPERTY_SKELETON_GROUPS = Object.freeze([3, 2, 3]);
const LEAD_PROPERTY_LAYOUT_SLOT = "lead_details_sidebar";
const createEmptyOwnerClaimDialog = () => ({
  open: false,
  loading: false,
  entryName: "",
  currentOwnerLabel: "",
});
const LEAD_COMPANY_PROPERTY_KEYS = Object.freeze([
  "id",
  "company_id",
  "old_reference",
  "name",
  "legal_name",
  "fiscal_code",
  "vat_number",
  "registration_number",
  "iban",
  "business_category",
  "industry",
  "company_size",
  "employees_count",
  "annual_revenue",
  "website",
  "email_primary",
  "email_secondary",
  "email_tertiary",
  "email_primary_label",
  "email_secondary_label",
  "email_tertiary_label",
  "phone_primary",
  "phone_secondary",
  "phone_tertiary",
  "phone_primary_label",
  "phone_secondary_label",
  "phone_tertiary_label",
  "address_text",
  "country",
  "linkedin",
  "facebook",
  "instagram",
  "administrator_name",
  "sales_manager_id",
  "source",
  "tags",
  "notes",
  "owner_user_id",
  "created_by_user_id",
  "updated_by_user_id",
  "created_at",
  "updated_at",
]);
const LEAD_PERSON_PROPERTY_KEYS = Object.freeze([
  "id",
  "company_id",
  "old_reference",
  "ncrm_company_id",
  "first_name",
  "last_name",
  "position",
  "department",
  "email_primary",
  "email_secondary",
  "email_tertiary",
  "email_primary_label",
  "email_secondary_label",
  "email_tertiary_label",
  "phone_primary",
  "phone_secondary",
  "phone_tertiary",
  "phone_primary_label",
  "phone_secondary_label",
  "phone_tertiary_label",
  "website",
  "linkedin",
  "facebook",
  "instagram",
  "address_text",
  "country",
  "source",
  "tags",
  "notes",
  "owner_user_id",
  "created_by_user_id",
  "updated_by_user_id",
  "created_at",
  "updated_at",
]);
const LEAD_COMPANY_CONTACT_PROPERTY_KEYS = new Set([
  "email_primary",
  "email_secondary",
  "email_tertiary",
  "email_primary_label",
  "email_secondary_label",
  "email_tertiary_label",
  "phone_primary",
  "phone_secondary",
  "phone_tertiary",
  "phone_primary_label",
  "phone_secondary_label",
  "phone_tertiary_label",
  "website",
  "linkedin",
  "facebook",
  "instagram",
  "administrator_name",
]);
const LEAD_PERSON_CONTACT_PROPERTY_KEYS = new Set([
  "first_name",
  "last_name",
  "email_primary",
  "email_secondary",
  "email_tertiary",
  "email_primary_label",
  "email_secondary_label",
  "email_tertiary_label",
  "phone_primary",
  "phone_secondary",
  "phone_tertiary",
  "phone_primary_label",
  "phone_secondary_label",
  "phone_tertiary_label",
  "website",
  "linkedin",
  "facebook",
  "instagram",
]);
const LEAD_COMPANY_ADDRESS_PROPERTY_KEYS = new Set(["address_text", "country"]);
const LEAD_PERSON_ADDRESS_PROPERTY_KEYS = new Set(["address_text", "country"]);
const LEAD_COMPANY_SYSTEM_PROPERTY_KEYS = new Set([
  "id",
  "company_id",
  "old_reference",
  "owner_user_id",
  "created_by_user_id",
  "updated_by_user_id",
  "created_at",
  "updated_at",
]);
const LEAD_PERSON_SYSTEM_PROPERTY_KEYS = new Set([
  "id",
  "company_id",
  "old_reference",
  "ncrm_company_id",
  "owner_user_id",
  "created_by_user_id",
  "updated_by_user_id",
  "created_at",
  "updated_at",
]);
export default {
  name: "NcrmLeadDetails",
  components: {
    NcrmRecordHeader,
    NcrmRecordLayout,
    NcrmActivityTimeline,
    NcrmRecordPropertiesCard,
    NcrmPropertyLayoutDrawer,
    NcrmOwnerClaimDialog,
    NcrmLeadConvertDialog,
    NcrmLeadContextActions,
    NcrmLeadHeaderActions,
    NcrmMailjetActivityPanel,
  },
  data() {
    return {
      loading: false,
      historyLoading: false,
      quickActionLoading: false,
      currentUserId: null,
      record: null,
      sourceRecord: null,
      board: null,
      boardStages: [],
      dealPipelines: [],
      owners: [],
      historyItems: [],
      historyLoaded: false,
      leadActivities: [],
      leadActivitiesLoaded: false,
      historyEventFilter: "all",
      mainTab: "activity",
      dialogs: {
        convert: false,
      },
      ownerClaimDialog: createEmptyOwnerClaimDialog(),
      menus: {
        owner: false,
        stage: false,
      },
      quickActions: {
        stageId: null,
        ownerUserId: null,
        dealName: "",
        dealPipelineId: null,
        dealStageId: null,
        convertStageId: null,
        convertOwnerUserId: null,
        convertNote: "",
      },
      pendingStageChange: null,
      pendingStageChangeClearTimerId: null,
      snackbar: {
        show: false,
        color: "info",
        message: "",
      },
      propertyLayoutDrawer: false,
      propertyLayoutLoading: false,
      propertyLayoutSaving: false,
      propertyLayoutLoaded: false,
      propertyLayoutAvailableKeys: [],
      propertyLayoutVisibleKeys: [],
      propertyLayoutRequestNonce: 0,
    };
  },
  computed: {
    emptyValueLabel() {
      return this.$t("ncrm.contacts.details.propertyEmpty.notSet");
    },
    leadId() {
      return parsePositiveId(this.$route?.params?.id);
    },
    recordTitle() {
      const label = resolveNcrmLeadEntryDisplayName(this.record || {});
      if (label && label !== "-") return label;
      return this.$t("ncrm.leads.details.titleFallback", { id: this.leadId || "-" });
    },
    recordSubtitle() {
      const boardLabel = normalizeNcrmLeadString(this.board?.name);
      const stageLabel = normalizeNcrmLeadString(this.record?.stage_name);
      return [boardLabel, stageLabel].filter(Boolean).join(" • ");
    },
    headerChips() {
      if (!this.record) return [];
      const entityType = normalizeNcrmLeadEntryEntityType(this.record.entity_type);
      const status = normalizeNcrmLeadEntryStatus(this.record.status, "open") || "open";
      const stageName = normalizeNcrmLeadString(this.record.stage_name);
      const chips = [];
      if (entityType) {
        chips.push({
          text: this.$t(`ncrm.leads.entityTypes.${entityType}`),
          color: "primary",
          variant: "tonal",
        });
      }
      chips.push({
        text: this.$t(`ncrm.leads.statuses.${status}`),
        color: this.statusChipColor(status),
        variant: "tonal",
      });
      const priority = normalizeNcrmLeadEntryPriority(this.record.priority);
      chips.push({
        text: this.$t(`ncrm.leads.priorities.${priority}`),
        color: resolveNcrmLeadEntryPriorityColor(priority),
        variant: "tonal",
        icon: "mdi-flag-outline",
      });
      if (stageName) {
        chips.push({
          text: stageName,
          color: "primary",
          variant: "outlined",
        });
      }
      chips.push({
        text: this.$t("ncrm.leads.details.chips.openActivities", {
          count: this.openLeadActivitiesCountLabel,
        }),
        color: this.openLeadActivitiesCount > 0 ? "warning" : "neutral",
        variant: "tonal",
        icon: "mdi-calendar-check-outline",
      });
      return chips;
    },
    quickStageOptions() {
      return (this.boardStages || [])
        .filter((stage) => Number(stage?.is_active) === 1 || stage?.is_active === true)
        .map((stage) => ({
          id: parsePositiveId(stage?.id),
          label: normalizeNcrmLeadString(stage?.name) || `#${parsePositiveId(stage?.id) || "-"}`,
          stage_key: normalizeNcrmLeadString(stage?.stage_key).toLowerCase(),
          stage_type: normalizeNcrmLeadString(stage?.stage_type).toLowerCase() || "open",
        }))
        .filter((entry) => entry.id);
    },
    quickConvertStageOptions() {
      const conversionOnly = this.quickStageOptions.filter((stage) => stage.stage_type === "conversion");
      return conversionOnly.length ? conversionOnly : this.quickStageOptions;
    },
    quickDealPipelineOptions() {
      return (this.dealPipelines || [])
        .filter((pipeline) => Number(pipeline?.is_active) === 1 || pipeline?.is_active === true)
        .map((pipeline) => ({
          id: parsePositiveId(pipeline?.id),
          label: normalizeNcrmLeadString(pipeline?.name) || `#${parsePositiveId(pipeline?.id) || "-"}`,
        }))
        .filter((entry) => entry.id);
    },
    selectedDealPipelineId() {
      const selected = parsePositiveId(this.quickActions.dealPipelineId);
      if (selected && this.quickDealPipelineOptions.some((option) => option.id === selected)) {
        return selected;
      }
      return this.quickDealPipelineOptions[0]?.id || null;
    },
    quickDealStageOptions() {
      const pipelineId = parsePositiveId(this.selectedDealPipelineId);
      if (!pipelineId) return [];
      const pipeline = (this.dealPipelines || []).find((entry) => parsePositiveId(entry?.id) === pipelineId);
      if (!pipeline) return [];
      return (Array.isArray(pipeline?.stages) ? pipeline.stages : [])
        .filter((stage) => Number(stage?.is_active) === 1 || stage?.is_active === true)
        .map((stage) => ({
          id: parsePositiveId(stage?.id),
          label: normalizeNcrmLeadString(stage?.name) || `#${parsePositiveId(stage?.id) || "-"}`,
          stage_key: normalizeNcrmLeadString(stage?.stage_key).toLowerCase(),
          stage_type: normalizeNcrmLeadString(stage?.stage_type).toLowerCase() || "open",
        }))
        .filter((entry) => entry.id);
    },
    quickOwnerOptions() {
      return (this.owners || [])
        .map((owner) => ({
          id: parsePositiveId(owner?.id),
          label:
            normalizeNcrmLeadString(owner?.display_name) ||
            normalizeNcrmLeadString(owner?.name) ||
            normalizeNcrmLeadString(owner?.email) ||
            `#${parsePositiveId(owner?.id) || "-"}`,
        }))
        .filter((entry) => entry.id);
    },
    currentOwnerUserId() {
      return parsePositiveId(this.record?.owner_user_id);
    },
    currentStageId() {
      return parsePositiveId(this.record?.stage_id);
    },
    currentStageLabel() {
      return normalizeNcrmLeadString(this.record?.stage_name) || this.emptyValueLabel;
    },
    currentStatusLabel() {
      const status = normalizeNcrmLeadEntryStatus(this.record?.status, "open") || "open";
      return this.$t(`ncrm.leads.statuses.${status}`);
    },
    canConvertLead() {
      return normalizeNcrmLeadEntryStatus(this.record?.status, "open") !== "converted";
    },
    canSaveConvertAction() {
      if (!this.canConvertLead) return false;
      const dealPipelineId = parsePositiveId(this.quickActions.dealPipelineId || this.selectedDealPipelineId);
      const dealStageId = parsePositiveId(this.quickActions.dealStageId);
      return Boolean(dealPipelineId && dealStageId);
    },
    selectMenuProps() {
      return {
        maxHeight: 320,
        contentClass: "ncrm-lead-board-select-menu",
        zIndex: 2710,
      };
    },
    groupedHistoryItems() {
      const groupsMap = new Map();
      for (const item of this.filteredHistoryItems || []) {
        const groupKey = this.historyGroupKey(item?.created_at);
        if (!groupsMap.has(groupKey)) {
          groupsMap.set(groupKey, {
            key: groupKey,
            label: this.formatHistoryGroupLabel(item?.created_at),
            items: [],
          });
        }
        groupsMap.get(groupKey).items.push(item);
      }
      return Array.from(groupsMap.values());
    },
    historyEventFilterItems() {
      return [
        {
          value: "all",
          label: this.$t("ncrm.leads.details.history.filters.all"),
        },
        {
          value: "stage_changed",
          label: this.historyEventLabel("stage_changed"),
        },
        {
          value: "owner_changed",
          label: this.historyEventLabel("owner_changed"),
        },
        {
          value: "converted_to_deal",
          label: this.historyEventLabel("converted_to_deal"),
        },
        {
          value: "lead_auto_converted_to_deal",
          label: this.historyEventLabel("lead_auto_converted_to_deal"),
        },
        {
          value: "activity_created",
          label: this.historyEventLabel("activity_created"),
        },
        {
          value: "activity_updated",
          label: this.historyEventLabel("activity_updated"),
        },
        {
          value: "activity_completed",
          label: this.historyEventLabel("activity_completed"),
        },
        {
          value: "activity_reopened",
          label: this.historyEventLabel("activity_reopened"),
        },
        {
          value: "activity_deleted",
          label: this.historyEventLabel("activity_deleted"),
        },
      ];
    },
    filteredHistoryItems() {
      const normalizedFilter = normalizeNcrmLeadString(this.historyEventFilter).toLowerCase();
      if (!normalizedFilter || normalizedFilter === "all") return this.historyItems || [];
      return (this.historyItems || []).filter((item) => {
        const eventType = normalizeNcrmLeadString(item?.event_type).toLowerCase();
        return eventType === normalizedFilter;
      });
    },
    nextBestAction() {
      const status = normalizeNcrmLeadEntryStatus(this.record?.status, "open") || "open";
      if (status === "converted") {
        return {
          type: "success",
          message: this.$t("ncrm.leads.details.context.nextActions.converted"),
        };
      }
      if (status === "failed" || status === "disqualified" || status === "no_response") {
        return {
          type: "warning",
          message: this.$t("ncrm.leads.details.context.nextActions.closed"),
        };
      }
      const nextFollowupAt = this.parseDateTime(this.resolvedNextFollowupAt);
      const todayStart = new Date();
      todayStart.setHours(0, 0, 0, 0);
      if (nextFollowupAt) {
        if (nextFollowupAt.getTime() < todayStart.getTime()) {
          return {
            type: "error",
            message: this.$t("ncrm.leads.details.context.nextActions.overdue"),
          };
        }
        const tomorrowStart = new Date(todayStart);
        tomorrowStart.setDate(tomorrowStart.getDate() + 1);
        if (nextFollowupAt.getTime() < tomorrowStart.getTime()) {
          return {
            type: "info",
            message: this.$t("ncrm.leads.details.context.nextActions.today"),
          };
        }
        return {
          type: "info",
          message: this.$t("ncrm.leads.details.context.nextActions.scheduled"),
        };
      }
      return {
        type: "warning",
        message: this.$t("ncrm.leads.details.context.nextActions.missing"),
      };
    },
    nextBestActionIcon() {
      const normalizedType = normalizeNcrmLeadString(this.nextBestAction?.type).toLowerCase();
      if (normalizedType === "error") return "mdi-alert-circle-outline";
      if (normalizedType === "warning") return "mdi-alert-outline";
      if (normalizedType === "success") return "mdi-check-circle-outline";
      return "mdi-information-outline";
    },
    leadScopedActivities() {
      const activities = Array.isArray(this.leadActivities) ? this.leadActivities : [];
      const targetLeadId = this.leadId;
      if (!targetLeadId) return activities;
      const leadScoped = activities.filter((activity) => {
        const originType = normalizeNcrmLeadString(activity?.origin_entity_type).toLowerCase();
        const originId = parsePositiveId(activity?.origin_entity_id);
        return originType === "lead_entry" && originId === targetLeadId;
      });
      // Backward compatibility for older activity entries created before origin metadata existed.
      return leadScoped.length ? leadScoped : activities;
    },
    openLeadActivitiesCount() {
      return this.leadScopedActivities.reduce((count, activity) => {
        return isNcrmActivityOpenStatus(activity?.activity_type, activity?.status) ? count + 1 : count;
      }, 0);
    },
    openLeadActivitiesCountLabel() {
      return this.leadActivitiesLoaded ? String(this.openLeadActivitiesCount) : "...";
    },
    shouldRequireStageActivityBeforeChange() {
      return this.board?.auto_create_task_if_missing_after_change === true ||
        Number(this.board?.auto_create_task_if_missing_after_change || 0) === 1;
    },
    openFollowupActivities() {
      return this.leadScopedActivities
        .filter((activity) => {
          if (!activity?.due_at) return false;
          return isNcrmActivityOpenStatus(activity.activity_type, activity.status);
        })
        .sort((left, right) => {
          const leftTime = this.parseDateTime(left?.due_at)?.getTime() || Number.POSITIVE_INFINITY;
          const rightTime = this.parseDateTime(right?.due_at)?.getTime() || Number.POSITIVE_INFINITY;
          return leftTime - rightTime;
        });
    },
    nextFollowupActivity() {
      return this.openFollowupActivities[0] || null;
    },
    resolvedNextFollowupAt() {
      if (this.nextFollowupActivity?.due_at) return this.nextFollowupActivity.due_at;
      if (!this.leadActivitiesLoaded) return normalizeNcrmLeadString(this.record?.next_followup_at);
      return "";
    },
    sourceRoute() {
      return resolveNcrmLeadEntrySourceRoute(this.record || {});
    },
    sourceEntityType() {
      return normalizeNcrmLeadEntryEntityType(this.record?.entity_type);
    },
    sourceEntityId() {
      if (this.sourceEntityType === "company") {
        return parsePositiveId(this.record?.ncrm_company_id);
      }
      if (this.sourceEntityType === "person") {
        return parsePositiveId(this.record?.ncrm_person_id);
      }
      return null;
    },
    sourcePropertiesTitle() {
      if (this.sourceEntityType === "company") {
        return this.$t("ncrm.contacts.details.sections.companyProperties");
      }
      if (this.sourceEntityType === "person") {
        return this.$t("ncrm.contacts.details.sections.personProperties");
      }
      return this.$t("ncrm.leads.details.sections.sourceProperties");
    },
    sourcePropertyLayoutEndpoint() {
      return buildNcrmPropertyLayoutEndpoint(this.sourceEntityType, LEAD_PROPERTY_LAYOUT_SLOT);
    },
    propertySkeletonGroups() {
      return PROPERTY_SKELETON_GROUPS.slice();
    },
    sourceRecordLabel() {
      if (!this.record) return this.emptyValueLabel;
      if (this.sourceEntityType === "company") {
        return (
          normalizeNcrmLeadString(this.sourceRecord?.name) ||
          normalizeNcrmLeadString(this.record.company_name) ||
          this.emptyValueLabel
        );
      }
      if (this.sourceEntityType === "person") {
        const firstName = normalizeNcrmLeadString(this.sourceRecord?.first_name);
        const lastName = normalizeNcrmLeadString(this.sourceRecord?.last_name);
        const sourceName = [firstName, lastName].filter(Boolean).join(" ").trim();
        return sourceName || normalizeNcrmLeadString(this.record.person_name) || this.emptyValueLabel;
      }
      return this.emptyValueLabel;
    },
    activityEntityType() {
      return this.sourceEntityType;
    },
    activityEntityId() {
      return this.sourceEntityId;
    },
    canShowActivityTimeline() {
      return Boolean(this.activityEntityType && this.activityEntityId);
    },
    sourcePropertyRows() {
      const source = this.sourceRecord && typeof this.sourceRecord === "object" ? this.sourceRecord : {};
      const propertyKeys =
        this.sourceEntityType === "company"
          ? LEAD_COMPANY_PROPERTY_KEYS
          : this.sourceEntityType === "person"
            ? LEAD_PERSON_PROPERTY_KEYS
            : [];
      return propertyKeys.map((key) => ({
        key,
        label: this.resolveSourcePropertyLabel(key),
        value: this.formatSourcePropertyValue(key, source?.[key]),
      }));
    },
    sourceCustomPropertyRows() {
      const sourceCustomFields = Array.isArray(this.sourceRecord?.custom_fields)
        ? this.sourceRecord.custom_fields
        : [];
      return sourceCustomFields
        .map((entry) => {
          const customKey = this.buildCustomLayoutKey(entry?.key || entry?.field_definition_id);
          if (!customKey) return null;
          return {
            key: customKey,
            label: normalizeNcrmLeadString(entry?.label) || this.resolveSourcePropertyLabel(customKey),
            value: normalizeNcrmLeadString(entry?.value_text) || this.emptyValueLabel,
          };
        })
        .filter(Boolean);
    },
    sourcePropertiesRowsForLayout() {
      return [...this.sourcePropertyRows, ...this.sourceCustomPropertyRows];
    },
    sourcePropertyLayoutItems() {
      return resolveNcrmPropertyLayoutItems(this.sourcePropertiesRowsForLayout);
    },
    sourcePropertyRowMap() {
      return buildNcrmPropertyRowMap(this.sourcePropertiesRowsForLayout);
    },
    sourcePropertiesRowsForDisplay() {
      return resolveNcrmOrderedPropertyRows({
        rows: this.sourcePropertiesRowsForLayout,
        rowMap: this.sourcePropertyRowMap,
        availableKeys: this.propertyLayoutAvailableKeys,
        visibleKeys: this.propertyLayoutVisibleKeys,
      });
    },
    sourcePropertySectionsForDisplay() {
      return this.buildPropertySections(this.sourcePropertiesRowsForDisplay, this.resolveSourcePropertySection);
    },
    overviewRows() {
      const record = this.record || {};
      const entityType = normalizeNcrmLeadEntryEntityType(record.entity_type);
      return [
        {
          key: "board",
          label: this.$t("ncrm.leads.details.fields.board"),
          value: normalizeNcrmLeadString(this.board?.name),
        },
        {
          key: "stage",
          label: this.$t("ncrm.leads.details.fields.stage"),
          value: normalizeNcrmLeadString(record.stage_name),
        },
        {
          key: "status",
          label: this.$t("ncrm.leads.details.fields.status"),
          value: this.$t(`ncrm.leads.statuses.${normalizeNcrmLeadEntryStatus(record.status, "open") || "open"}`),
        },
        {
          key: "owner",
          label: this.$t("ncrm.leads.details.fields.owner"),
          value: this.ownerLabel(record),
        },
        {
          key: "entity_type",
          label: this.$t("ncrm.leads.details.fields.entityType"),
          value: entityType ? this.$t(`ncrm.leads.entityTypes.${entityType}`) : "",
        },
        {
          key: "source_record",
          label: this.$t("ncrm.leads.details.fields.sourceRecord"),
          value: this.sourceRecordLabel,
        },
        {
          key: "next_followup",
          label: this.$t("ncrm.leads.details.fields.nextFollowup"),
          value: this.formatDateTime(this.resolvedNextFollowupAt),
        },
        {
          key: "updated_at",
          label: this.$t("ncrm.leads.details.fields.updatedAt"),
          value: this.formatDateTime(record.updated_at),
        },
      ].filter((entry) => normalizeNcrmLeadString(entry.value));
    },
  },
  watch: {
    leadId: {
      immediate: true,
      handler() {
        this.fetchRecord();
      },
    },
    "$route.query.tab": {
      immediate: true,
      handler() {
        this.applyMainTabFromRoute();
      },
    },
    mainTab(value) {
      this.syncRouteTab(value);
      if (value === "history") {
        this.fetchHistory();
      }
    },
    sourceEntityType(nextValue, previousValue) {
      if (nextValue === previousValue) return;
      this.propertyLayoutDrawer = false;
      this.resetPropertyLayoutState();
    },
    sourcePropertyLayoutItems: {
      deep: true,
      handler() {
        this.syncPropertyLayoutStateFromRows();
      },
    },
  },
  beforeUnmount() {
    this.clearPendingStageChange();
  },
  mounted() {
    this.fetchCurrentUser();
  },
  methods: {
    onMailjetNotify(payload) {
      if (!payload) return;
      if (typeof this.showSnackbar === "function") {
        this.showSnackbar(payload.message, payload.color || "info");
      }
    },
    resolveMainTabFromRoute() {
      const rawTab = normalizeNcrmLeadString(this.$route?.query?.tab).toLowerCase();
      if (LEAD_DETAILS_TABS.includes(rawTab)) return rawTab;
      return "";
    },
    applyMainTabFromRoute() {
      const routeTab = this.resolveMainTabFromRoute();
      if (!routeTab || routeTab === this.mainTab) return;
      this.mainTab = routeTab;
    },
    syncRouteTab(tab) {
      const normalized = LEAD_DETAILS_TABS.includes(tab) ? tab : "activity";
      const current = normalizeNcrmLeadString(this.$route?.query?.tab).toLowerCase();
      if (current === normalized) return;
      this.$router
        .replace({
          query: {
            ...(this.$route?.query || {}),
            tab: normalized,
          },
        })
        .catch(() => {});
    },
    statusChipColor(status) {
      const normalized = normalizeNcrmLeadEntryStatus(status, "open") || "open";
      if (normalized === "open") return "primary";
      if (normalized === "converted") return "success";
      if (normalized === "failed") return "error";
      if (normalized === "disqualified") return "warning";
      if (normalized === "no_response") return "neutral";
      return "primary";
    },
    ownerLabel(entry = {}) {
      const source = entry && typeof entry === "object" ? entry : {};
      const label = normalizeNcrmLeadString(source.owner_name);
      if (label) return label;
      const ownerId = parsePositiveId(source.owner_user_id);
      return ownerId ? `#${ownerId}` : this.emptyValueLabel;
    },
    ownerOptionLabel(ownerUserId) {
      const normalizedOwnerId = parsePositiveId(ownerUserId);
      if (!normalizedOwnerId) return this.emptyValueLabel;
      const option = this.quickOwnerOptions.find((entry) => entry.id === normalizedOwnerId);
      return option?.label || `#${normalizedOwnerId}`;
    },
    onOwnerClaimDialogModelUpdate(value) {
      if (value) {
        this.ownerClaimDialog.open = true;
        return;
      }
      if (this.ownerClaimDialog.loading) return;
      this.ownerClaimDialog = createEmptyOwnerClaimDialog();
    },
    async fetchCurrentUser() {
      const existingUserId = parsePositiveId(this.currentUserId);
      if (existingUserId) return existingUserId;
      const resolvedId = await resolveCurrentUserId(axios);
      this.currentUserId = resolvedId || null;
      return this.currentUserId;
    },
    async maybeOpenOwnerClaimDialog({ ownerUserId, ownerLabel } = {}) {
      const currentUserId = await this.fetchCurrentUser();
      const normalizedOwnerId = parsePositiveId(ownerUserId);
      if (!currentUserId || !normalizedOwnerId || currentUserId === normalizedOwnerId) {
        return;
      }
      this.ownerClaimDialog = {
        open: true,
        loading: false,
        entryName: this.recordTitle,
        currentOwnerLabel: normalizeNcrmLeadString(ownerLabel) || this.ownerOptionLabel(normalizedOwnerId),
      };
    },
    async confirmOwnerClaim() {
      const currentUserId = await this.fetchCurrentUser();
      if (!this.leadId || !currentUserId || this.ownerClaimDialog.loading) return;
      this.ownerClaimDialog.loading = true;
      try {
        await axios.patch(`/ncrm/leads/entries/${this.leadId}`, {
          owner_user_id: currentUserId,
        });
        this.ownerClaimDialog = createEmptyOwnerClaimDialog();
        this.historyLoaded = false;
        await this.fetchRecord();
        this.showSnackbar(this.$t("ncrm.leads.ownerClaimDialog.messages.success"), "success");
      } catch (error) {
        if (isAxiosRequestAborted(error)) return;
        console.error("[ncrm-lead-details] claim owner error:", error);
        this.showSnackbar(this.$t("ncrm.leads.ownerClaimDialog.messages.error"), "error");
      } finally {
        if (this.ownerClaimDialog.open) {
          this.ownerClaimDialog.loading = false;
        }
      }
    },
    formatDateTime(value) {
      const locale = this.$i18n?.locale === "en" ? "en-US" : this.$i18n?.locale === "ru" ? "ru-RU" : "ro-RO";
      return formatNcrmLeadDateTime(value, locale);
    },
    parseDateTime(value) {
      const normalized = normalizeNcrmLeadString(value);
      if (!normalized) return null;
      const parsed = new Date(normalized.replace(" ", "T"));
      if (Number.isNaN(parsed.getTime())) return null;
      return parsed;
    },
    async openNextFollowupComposer() {
      if (!this.canShowActivityTimeline) {
        this.showSnackbar(this.$t("ncrm.leads.details.activity.unavailable"), "warning");
        return;
      }
      this.mainTab = "activity";
      await this.$nextTick();
      await this.$nextTick();
      const timeline = this.$refs.leadActivityTimeline;
      if (!timeline || typeof timeline.openComposerWithPreset !== "function") {
        this.showSnackbar(this.$t("ncrm.leads.details.messages.quickActionError"), "error");
        return;
      }
      const preset = {
        activity_type: "todo",
      };
      if (this.resolvedNextFollowupAt) {
        preset.due_at = this.resolvedNextFollowupAt;
      }
      timeline.openComposerWithPreset(preset);
    },
    onLeadActivitiesLoaded(items) {
      this.leadActivities = normalizeNcrmActivityList(items);
      this.leadActivitiesLoaded = true;
    },
    onLeadActivityComposerToggle(isOpen) {
      if (isOpen) return;
      if (!this.pendingStageChange) return;
      if (this.pendingStageChangeClearTimerId) {
        clearTimeout(this.pendingStageChangeClearTimerId);
      }
      // The timeline emits close before created on save; clear on next tick to
      // allow created-handler to commit pending stage change.
      this.pendingStageChangeClearTimerId = setTimeout(() => {
        this.clearPendingStageChange();
      }, 0);
    },
    clearPendingStageChange() {
      if (this.pendingStageChangeClearTimerId) {
        clearTimeout(this.pendingStageChangeClearTimerId);
      }
      this.pendingStageChangeClearTimerId = null;
      this.pendingStageChange = null;
    },
    isFailedStageOption(stageOption = {}) {
      const stageKey = normalizeNcrmLeadString(stageOption?.stage_key).toLowerCase();
      if (stageKey === "failed" || stageKey === "faild") return true;
      const stageLabel = normalizeNcrmLeadString(stageOption?.label).toLowerCase();
      return stageLabel === "failed" || stageLabel === "faild";
    },
    buildStageActivityPreset(stageOption = {}) {
      const isFailedStage = this.isFailedStageOption(stageOption);
      if (isFailedStage) {
        return {
          activity_type: "note",
          allowed_activity_types: ["note"],
          lock_activity_type: true,
        };
      }
      return {
        activity_type: "todo",
        allowed_activity_types: ["todo", "call", "email", "meeting"],
        notice_message: this.$t("ncrm.leads.details.messages.stageRequiresActivity"),
        notice_color: "warning",
      };
    },
    async openStageChangeActivityComposer(stageOption = {}) {
      if (!this.canShowActivityTimeline) {
        this.showSnackbar(this.$t("ncrm.leads.details.activity.unavailable"), "warning");
        return;
      }
      this.mainTab = "activity";
      await this.$nextTick();
      await this.$nextTick();
      const timeline = this.$refs.leadActivityTimeline;
      if (!timeline || typeof timeline.openComposerWithPreset !== "function") {
        this.showSnackbar(this.$t("ncrm.leads.details.messages.quickActionError"), "error");
        return;
      }
      this.clearPendingStageChange();
      this.pendingStageChange = {
        stageId: parsePositiveId(stageOption?.id),
        requiresNote: this.isFailedStageOption(stageOption),
      };
      timeline.openComposerWithPreset(this.buildStageActivityPreset(stageOption));
    },
    async onLeadActivityCreated(activity) {
      if (this.pendingStageChangeClearTimerId) {
        clearTimeout(this.pendingStageChangeClearTimerId);
        this.pendingStageChangeClearTimerId = null;
      }
      const pendingStageChange = this.pendingStageChange;
      if (!pendingStageChange?.stageId) return;
      const activityType = normalizeNcrmActivityType(activity?.activity_type) || "";
      const isValidActivityType = pendingStageChange.requiresNote
        ? activityType === "note"
        : ["todo", "call", "email", "meeting"].includes(activityType);
      if (!isValidActivityType) {
        this.clearPendingStageChange();
        this.showSnackbar(this.$t("ncrm.leads.details.messages.stageActivityInvalid"), "warning");
        return;
      }
      this.quickActions.stageId = pendingStageChange.stageId;
      this.clearPendingStageChange();
      await this.saveStageAction();
    },
    historyGroupKey(value) {
      const parsed = this.parseDateTime(value);
      if (!parsed) return "unknown";
      const year = parsed.getFullYear();
      const month = String(parsed.getMonth() + 1).padStart(2, "0");
      const day = String(parsed.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },
    formatHistoryGroupLabel(value) {
      const parsed = this.parseDateTime(value);
      if (!parsed) return this.$t("ncrm.leads.details.history.unknownDateGroup");
      return formatDisplayDate(parsed);
    },
    hasHistoryValueChange(item = {}) {
      const fromValue = normalizeNcrmLeadString(item?.from_value);
      const toValue = normalizeNcrmLeadString(item?.to_value);
      return Boolean(fromValue || toValue);
    },
    historyEventColor(eventType) {
      const normalized = normalizeNcrmLeadString(eventType).toLowerCase();
      if (normalized === "converted_to_deal") return "success";
      if (normalized === "lead_auto_converted_to_deal") return "success";
      if (normalized === "stage_changed") return "primary";
      if (normalized === "owner_changed") return "secondary";
      if (normalized === "closed") return "warning";
      if (normalized === "reopened") return "info";
      if (normalized === "activity_created") return "success";
      if (normalized === "activity_updated") return "primary";
      if (normalized === "activity_completed") return "success";
      if (normalized === "activity_reopened") return "info";
      if (normalized === "activity_deleted") return "error";
      return "primary";
    },
    historyChangedByLabel(item = {}) {
      return normalizeNcrmLeadString(item.changed_by_name) || this.emptyValueLabel;
    },
    historyEventLabel(eventType) {
      const normalized = normalizeNcrmLeadString(eventType).toLowerCase();
      if (!normalized) return this.$t("ncrm.leads.details.history.eventTypes.updated");
      const key = `ncrm.leads.details.history.eventTypes.${normalized}`;
      const translated = this.$t(key);
      return translated === key ? normalized : translated;
    },
    resetPropertyLayoutState() {
      this.propertyLayoutRequestNonce = Number(this.propertyLayoutRequestNonce || 0) + 1;
      this.propertyLayoutLoading = false;
      this.propertyLayoutSaving = false;
      this.propertyLayoutLoaded = false;
      this.propertyLayoutAvailableKeys = [];
      this.propertyLayoutVisibleKeys = [];
    },
    syncPropertyLayoutStateFromRows() {
      const { availableKeys, visibleKeys } = resolveNcrmPropertyLayoutState({
        rows: this.sourcePropertiesRowsForLayout,
        fallbackVisibleKeys: this.propertyLayoutVisibleKeys,
      });
      this.propertyLayoutAvailableKeys = availableKeys;
      this.propertyLayoutVisibleKeys = visibleKeys;
      if (!availableKeys.length) {
        this.propertyLayoutLoaded = false;
      }
    },
    applyPropertyLayoutResponse(payload = {}) {
      const { availableKeys, visibleKeys } = resolveNcrmPropertyLayoutState({
        rows: this.sourcePropertiesRowsForLayout,
        payload,
        fallbackVisibleKeys: this.propertyLayoutVisibleKeys,
      });
      this.propertyLayoutAvailableKeys = availableKeys;
      this.propertyLayoutVisibleKeys = visibleKeys;
      this.propertyLayoutLoaded = true;
    },
    async fetchPropertyLayout({ force = false, silent = false } = {}) {
      const endpoint = this.sourcePropertyLayoutEndpoint;
      if (!endpoint) {
        this.resetPropertyLayoutState();
        return;
      }
      if (this.propertyLayoutLoading) return;
      if (this.propertyLayoutLoaded && !force) {
        this.syncPropertyLayoutStateFromRows();
        return;
      }
      const requestNonce = Number(this.propertyLayoutRequestNonce || 0) + 1;
      this.propertyLayoutRequestNonce = requestNonce;
      this.propertyLayoutLoading = true;
      try {
        const { data } = await axios.get(endpoint);
        if (requestNonce !== this.propertyLayoutRequestNonce) return;
        this.applyPropertyLayoutResponse(data || {});
      } catch (error) {
        if (isAxiosRequestAborted(error)) return;
        if (requestNonce !== this.propertyLayoutRequestNonce) return;
        console.error("[ncrm-lead-details] property layout load error:", error);
        this.propertyLayoutLoaded = false;
        this.syncPropertyLayoutStateFromRows();
        if (!silent) {
          this.showSnackbar(
            error?.response?.data?.error || this.$t("ncrm.contacts.details.propertyLayout.messages.loadError"),
            "error"
          );
        }
      } finally {
        if (requestNonce === this.propertyLayoutRequestNonce) {
          this.propertyLayoutLoading = false;
        }
      }
    },
    openPropertyLayoutDrawer() {
      if (!this.sourcePropertyLayoutEndpoint) return;
      this.propertyLayoutDrawer = true;
      this.fetchPropertyLayout({ force: !this.propertyLayoutLoaded, silent: false });
    },
    async savePropertyLayout(visibleKeys) {
      const endpoint = this.sourcePropertyLayoutEndpoint;
      if (!endpoint) return;
      const availableKeys = dedupeNcrmPropertyLayoutKeys(this.sourcePropertyLayoutItems.map((entry) => entry.key));
      const payloadKeys = sanitizeNcrmPropertyLayoutVisibleKeys(visibleKeys, availableKeys);
      if (!payloadKeys.length) {
        this.showSnackbar(this.$t("ncrm.contacts.details.propertyLayout.requiredWarning"), "warning");
        return;
      }
      const requestNonce = Number(this.propertyLayoutRequestNonce || 0) + 1;
      this.propertyLayoutRequestNonce = requestNonce;
      this.propertyLayoutSaving = true;
      try {
        const { data } = await axios.put(endpoint, {
          visible_property_keys: payloadKeys,
        });
        if (requestNonce !== this.propertyLayoutRequestNonce) return;
        this.applyPropertyLayoutResponse(data || {});
        this.propertyLayoutDrawer = false;
        this.showSnackbar(this.$t("ncrm.contacts.details.propertyLayout.messages.saveSuccess"), "success");
      } catch (error) {
        if (isAxiosRequestAborted(error)) return;
        if (requestNonce !== this.propertyLayoutRequestNonce) return;
        console.error("[ncrm-lead-details] property layout save error:", error);
        this.showSnackbar(
          error?.response?.data?.error || this.$t("ncrm.contacts.details.propertyLayout.messages.saveError"),
          "error"
        );
      } finally {
        if (requestNonce === this.propertyLayoutRequestNonce) {
          this.propertyLayoutSaving = false;
        }
      }
    },
    resolvePropertySectionLabel(sectionKey) {
      return this.$t(`ncrm.contacts.details.propertySections.${sectionKey}`);
    },
    resolvePropertySectionEmptyAction(sectionKey) {
      void sectionKey;
      // Lead details reuses source properties in read-only mode.
      // Empty section CTA is hidden here to avoid dead actions.
      return "";
    },
    buildCustomLayoutKey(rawKey) {
      const normalized = normalizeNcrmLeadString(rawKey);
      if (!normalized) return "";
      return `custom:${normalized}`;
    },
    isPropertyRowEmpty(value) {
      if (value === null || value === undefined) return true;
      if (typeof value === "number") return !Number.isFinite(value);
      const normalized = String(value).trim();
      if (!normalized) return true;
      return normalized === this.emptyValueLabel;
    },
    shouldDisplayPropertyRow(row) {
      const key = normalizeNcrmLeadString(row?.key);
      if (!key) return false;
      return !this.isPropertyRowEmpty(row?.value);
    },
    resolveSourcePropertySection(key) {
      const normalized = normalizeNcrmLeadString(key);
      if (!normalized) return "business";
      if (normalized.startsWith("custom:")) return "customFields";
      if (this.sourceEntityType === "company") {
        if (LEAD_COMPANY_SYSTEM_PROPERTY_KEYS.has(normalized)) return "system";
        if (LEAD_COMPANY_ADDRESS_PROPERTY_KEYS.has(normalized)) return "address";
        if (LEAD_COMPANY_CONTACT_PROPERTY_KEYS.has(normalized)) return "contact";
        return "business";
      }
      if (this.sourceEntityType === "person") {
        if (LEAD_PERSON_SYSTEM_PROPERTY_KEYS.has(normalized)) return "system";
        if (LEAD_PERSON_ADDRESS_PROPERTY_KEYS.has(normalized)) return "address";
        if (LEAD_PERSON_CONTACT_PROPERTY_KEYS.has(normalized)) return "contact";
        return "business";
      }
      return "business";
    },
    buildPropertySections(rows, resolveSectionKey) {
      const grouped = LEAD_PROPERTY_SECTION_ORDER.reduce((acc, sectionKey) => {
        acc[sectionKey] = {
          rows: [],
          emptyRows: [],
        };
        return acc;
      }, {});
      for (const row of rows || []) {
        const sectionKey = resolveSectionKey(String(row?.key || "").trim());
        const targetKey = grouped[sectionKey] ? sectionKey : "business";
        if (this.shouldDisplayPropertyRow(row)) {
          grouped[targetKey].rows.push(row);
          continue;
        }
        grouped[targetKey].emptyRows.push(row);
      }
      return LEAD_PROPERTY_SECTION_ORDER
        .map((sectionKey) => {
          const section = grouped[sectionKey];
          const hasRows = section.rows.length > 0;
          const hasEmptyRows = section.emptyRows.length > 0;
          if (!hasRows && !hasEmptyRows) return null;
          const emptyAction = hasRows ? "" : this.resolvePropertySectionEmptyAction(sectionKey);
          return {
            key: sectionKey,
            label: this.resolvePropertySectionLabel(sectionKey),
            rows: section.rows,
            emptyRows: section.emptyRows,
            emptyAction,
            emptyActionLabel: emptyAction ? this.$t(`ncrm.contacts.details.propertyEmpty.actions.${emptyAction}`) : "",
          };
        })
        .filter(Boolean);
    },
    resolveSourcePropertyRowLink(row) {
      return resolveNcrmPropertyLinkMeta(row?.key, row?.value); // câmpurile `custom:*` nu sunt legabile → null
    },
    canCopySourcePropertyRow(row) {
      const linkMeta = this.resolveSourcePropertyRowLink(row);
      return Boolean(linkMeta?.copyValue);
    },
    async copySourcePropertyRowValue(row) {
      const linkMeta = this.resolveSourcePropertyRowLink(row);
      const valueToCopy = normalizeNcrmLeadString(linkMeta?.copyValue);
      if (!valueToCopy) return;
      try {
        const copied = await this.copyTextToClipboard(valueToCopy);
        if (!copied) {
          throw new Error("Clipboard unavailable");
        }
        this.showSnackbar(this.$t("ncrm.contacts.details.propertyInteractions.copySuccess"), "success");
      } catch (error) {
        console.error("[ncrm-lead-details] copy property value failed:", error);
        this.showSnackbar(this.$t("ncrm.contacts.details.propertyInteractions.copyError"), "error");
      }
    },
    async copyTextToClipboard(value) {
      const normalized = String(value || "").trim();
      if (!normalized) return false;
      if (typeof navigator !== "undefined" && navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(normalized);
        return true;
      }
      return this.copyTextToClipboardFallback(normalized);
    },
    copyTextToClipboardFallback(value) {
      if (typeof document === "undefined" || !document?.body) return false;
      const textarea = document.createElement("textarea");
      textarea.value = value;
      textarea.setAttribute("readonly", "");
      textarea.style.position = "fixed";
      textarea.style.left = "-9999px";
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      let copied = false;
      if (typeof document.execCommand === "function") {
        copied = document.execCommand("copy");
      }
      document.body.removeChild(textarea);
      return copied;
    },
    resolveSourceTimestampTooltip(rowKeyOrRow) {
      const normalizedKey = normalizeNcrmLeadString(
        typeof rowKeyOrRow === "string" ? rowKeyOrRow : rowKeyOrRow?.key
      );
      if (normalizedKey !== "created_at" && normalizedKey !== "updated_at") return "";
      const tooltipValue = formatNcrmTimestampTooltip(this.sourceRecord?.[normalizedKey]);
      if (!tooltipValue) return "";
      return this.$t("ncrm.contacts.details.propertyInteractions.timestampTooltip", {
        value: tooltipValue,
      });
    },
    humanizeSourceFieldKey(key) {
      const normalized = normalizeNcrmLeadString(key);
      if (!normalized) return "";
      return normalized
        .replace(/_/g, " ")
        .replace(/\bid\b/g, "ID")
        .replace(/\b\w/g, (char) => char.toUpperCase());
    },
    resolveCompanySourcePropertyLabel(key) {
      const labelByField = {
        old_reference: this.$t("ncrm.contacts.details.company.fields.oldReference"),
        name: this.$t("ncrm.contacts.details.company.fields.name"),
        business_category: this.$t("ncrm.contacts.details.company.fields.businessCategory"),
        email_primary: this.$t("ncrm.contacts.details.company.fields.emailPrimary"),
        phone_primary: this.$t("ncrm.contacts.details.company.fields.phonePrimary"),
        website: this.$t("ncrm.contacts.details.company.fields.website"),
        source: this.$t("ncrm.contacts.details.company.fields.source"),
        company_id: this.$t("ncrm.contacts.details.company.fields.workspaceCompany"),
        sales_manager_id: this.$t("ncrm.contacts.details.company.fields.salesManager"),
        owner_user_id: this.$t("ncrm.contacts.details.company.fields.ownerUser"),
        created_by_user_id: this.$t("ncrm.contacts.details.company.fields.createdByUser"),
        updated_by_user_id: this.$t("ncrm.contacts.details.company.fields.updatedByUser"),
        address_text: this.$t("ncrm.contacts.details.company.fields.address"),
        country: this.$t("ncrm.contacts.details.company.fields.country"),
        notes: this.$t("ncrm.contacts.details.company.fields.notes"),
        created_at: this.$t("ncrm.contacts.details.company.fields.createdAt"),
        updated_at: this.$t("ncrm.contacts.details.company.fields.updatedAt"),
      };
      return labelByField[key] || this.humanizeSourceFieldKey(key);
    },
    resolvePersonSourcePropertyLabel(key) {
      const labelByField = {
        old_reference: this.$t("ncrm.contacts.details.person.fields.oldReference"),
        first_name: this.$t("ncrm.contacts.details.person.fields.firstName"),
        last_name: this.$t("ncrm.contacts.details.person.fields.lastName"),
        position: this.$t("ncrm.contacts.details.person.fields.position"),
        department: this.$t("ncrm.contacts.details.person.fields.department"),
        email_primary: this.$t("ncrm.contacts.details.person.fields.emailPrimary"),
        phone_primary: this.$t("ncrm.contacts.details.person.fields.phonePrimary"),
        source: this.$t("ncrm.contacts.details.person.fields.source"),
        company_id: this.$t("ncrm.contacts.details.person.fields.workspaceCompany"),
        ncrm_company_id: this.$t("ncrm.contacts.details.person.fields.company"),
        owner_user_id: this.$t("ncrm.contacts.details.person.fields.ownerUser"),
        created_by_user_id: this.$t("ncrm.contacts.details.person.fields.createdByUser"),
        updated_by_user_id: this.$t("ncrm.contacts.details.person.fields.updatedByUser"),
        address_text: this.$t("ncrm.contacts.details.person.fields.address"),
        country: this.$t("ncrm.contacts.details.person.fields.country"),
        notes: this.$t("ncrm.contacts.details.person.fields.notes"),
        created_at: this.$t("ncrm.contacts.details.person.fields.createdAt"),
        updated_at: this.$t("ncrm.contacts.details.person.fields.updatedAt"),
      };
      return labelByField[key] || this.humanizeSourceFieldKey(key);
    },
    resolveSourcePropertyLabel(key) {
      const normalized = normalizeNcrmLeadString(key);
      if (!normalized) return "";
      if (normalized.startsWith("custom:")) {
        return this.humanizeSourceFieldKey(normalized.slice("custom:".length));
      }
      if (this.sourceEntityType === "company") {
        return this.resolveCompanySourcePropertyLabel(normalized);
      }
      if (this.sourceEntityType === "person") {
        return this.resolvePersonSourcePropertyLabel(normalized);
      }
      return this.humanizeSourceFieldKey(normalized);
    },
    resolveSourceReferenceName(idValue, displayName) {
      const normalizedName = normalizeNcrmLeadString(displayName);
      if (normalizedName) return normalizedName;
      const normalizedId = normalizeNcrmLeadString(idValue);
      return normalizedId || this.emptyValueLabel;
    },
    formatSourcePropertyValue(key, value) {
      const normalizedKey = normalizeNcrmLeadString(key);
      const source = this.sourceRecord && typeof this.sourceRecord === "object" ? this.sourceRecord : {};
      if (normalizedKey === "company_id") {
        return this.resolveSourceReferenceName(value, source?.workspace_company_name);
      }
      if (normalizedKey === "created_by_user_id") {
        return this.resolveSourceReferenceName(value, source?.created_by_user_name);
      }
      if (normalizedKey === "updated_by_user_id") {
        return this.resolveSourceReferenceName(value, source?.updated_by_user_name);
      }
      if (normalizedKey === "owner_user_id") {
        return this.resolveSourceReferenceName(value, source?.owner_user_name);
      }
      if (this.sourceEntityType === "company" && normalizedKey === "sales_manager_id") {
        return this.resolveSourceReferenceName(value, source?.sales_manager_name);
      }
      if (this.sourceEntityType === "person" && normalizedKey === "ncrm_company_id") {
        return this.resolveSourceReferenceName(value, source?.company_name);
      }
      if (normalizedKey === "created_at" || normalizedKey === "updated_at") {
        return this.formatDateTime(value);
      }
      if (value === null || value === undefined) return this.emptyValueLabel;
      if (typeof value === "string") {
        const normalized = value.trim();
        return normalized || this.emptyValueLabel;
      }
      if (typeof value === "number" || typeof value === "boolean") {
        return String(value);
      }
      return normalizeNcrmLeadString(value) || this.emptyValueLabel;
    },
    resolveSourceEndpoint() {
      if (!this.sourceEntityType || !this.sourceEntityId) return "";
      if (this.sourceEntityType === "company") {
        return `/ncrm/companies/${this.sourceEntityId}`;
      }
      if (this.sourceEntityType === "person") {
        return `/ncrm/people/${this.sourceEntityId}`;
      }
      return "";
    },
    async fetchSourceRecord({ silent = true } = {}) {
      const endpoint = this.resolveSourceEndpoint();
      if (!endpoint) {
        this.sourceRecord = null;
        return;
      }
      try {
        const { data } = await axios.get(endpoint);
        this.sourceRecord = data && typeof data === "object" ? data : null;
      } catch (error) {
        if (isAxiosRequestAborted(error)) return;
        console.error("[ncrm-lead-details] source record load error:", error);
        this.sourceRecord = null;
        if (!silent) {
          this.showSnackbar(this.$t("ncrm.leads.details.messages.loadSourceError"), "warning");
        }
      }
    },
    showSnackbar(message, color = "info") {
      this.snackbar = {
        show: true,
        color,
        message: String(message || ""),
      };
    },
    async fetchQuickReferences({ silent = true } = {}) {
      const boardId = parsePositiveId(this.board?.id || this.record?.board_id);
      if (!boardId) {
        this.boardStages = [];
        this.dealPipelines = [];
        this.owners = [];
        return;
      }
      try {
        const [stagesResult, ownersResult, dealPipelinesResult] = await Promise.allSettled([
          axios.get(`/ncrm/leads/boards/${boardId}/stages`),
          axios.get("/ncrm/leads/owners", { params: { limit: 200 } }),
          axios.get("/ncrm/deals/pipelines", {
            params: {
              include_stages: true,
              include_inactive: false,
              ensure_default: true,
            },
          }),
        ]);
        const results = [stagesResult, ownersResult, dealPipelinesResult];
        if (
          results.some((result) => result.status === "rejected" && isAxiosRequestAborted(result.reason))
        ) {
          return;
        }
        const hasStageError = stagesResult.status === "rejected";
        const hasOwnersError = ownersResult.status === "rejected";
        const hasDealPipelinesError = dealPipelinesResult.status === "rejected";
        this.boardStages =
          stagesResult.status === "fulfilled" && Array.isArray(stagesResult.value?.data?.items)
            ? stagesResult.value.data.items
            : [];
        this.owners =
          ownersResult.status === "fulfilled" && Array.isArray(ownersResult.value?.data?.items)
            ? ownersResult.value.data.items
            : [];
        this.dealPipelines =
          dealPipelinesResult.status === "fulfilled" && Array.isArray(dealPipelinesResult.value?.data?.items)
            ? dealPipelinesResult.value.data.items
            : [];
        this.ensureConvertDealSelection();
        if (hasStageError || hasOwnersError || hasDealPipelinesError) {
          if (hasStageError) {
            console.error("[ncrm-lead-details] board stages load error:", stagesResult.reason);
          }
          if (hasOwnersError) {
            console.error("[ncrm-lead-details] owners load error:", ownersResult.reason);
          }
          if (hasDealPipelinesError) {
            console.error("[ncrm-lead-details] deal pipelines load error:", dealPipelinesResult.reason);
          }
          if (!silent) {
            this.showSnackbar(this.$t("ncrm.leads.details.messages.loadActionsError"), "warning");
          }
        }
      } catch (error) {
        if (isAxiosRequestAborted(error)) return;
        console.error("[ncrm-lead-details] quick references load error:", error);
        this.boardStages = [];
        this.dealPipelines = [];
        this.owners = [];
        if (!silent) {
          this.showSnackbar(this.$t("ncrm.leads.details.messages.loadActionsError"), "warning");
        }
      }
    },
    async applyStageInline(stageId) {
      const normalizedStageId = parsePositiveId(stageId);
      const currentStageId = parsePositiveId(this.record?.stage_id);
      if (!normalizedStageId || !this.leadId || normalizedStageId === currentStageId || this.quickActionLoading) {
        this.menus.stage = false;
        return;
      }
      const targetStage = this.quickStageOptions.find((option) => option.id === normalizedStageId) || null;
      this.menus.stage = false;
      if (!this.shouldRequireStageActivityBeforeChange && !this.isFailedStageOption(targetStage || {})) {
        this.quickActions.stageId = normalizedStageId;
        await this.saveStageAction();
        return;
      }
      await this.openStageChangeActivityComposer(targetStage || { id: normalizedStageId });
    },
    async applyOwnerInline(ownerUserId) {
      const normalizedOwnerId = parsePositiveId(ownerUserId);
      const currentOwnerId = parsePositiveId(this.record?.owner_user_id);
      if (!normalizedOwnerId || !this.leadId || normalizedOwnerId === currentOwnerId || this.quickActionLoading) {
        this.menus.owner = false;
        return;
      }
      this.quickActions.ownerUserId = normalizedOwnerId;
      this.menus.owner = false;
      await this.saveOwnerAction();
    },
    openConvertDialog() {
      this.quickActions.dealName = "";
      this.quickActions.convertNote = "";
      this.quickActions.convertOwnerUserId = parsePositiveId(this.record?.owner_user_id);
      const currentStageId = parsePositiveId(this.record?.stage_id);
      const conversionStage = this.quickConvertStageOptions[0];
      this.quickActions.convertStageId = conversionStage?.id || currentStageId || null;
      this.quickActions.dealPipelineId = this.selectedDealPipelineId;
      this.quickActions.dealStageId = this.quickDealStageOptions[0]?.id || null;
      this.ensureConvertDealSelection();
      this.dialogs.convert = true;
    },
    openConvertDialogWithDefaults(defaults = {}) {
      const defaultLeadStageId = parsePositiveId(defaults?.lead_stage_id);
      const defaultOwnerUserId = parsePositiveId(defaults?.owner_user_id);
      const defaultPipelineId = parsePositiveId(defaults?.deal_pipeline_id);
      const defaultDealStageId = parsePositiveId(defaults?.deal_stage_id);
      this.quickActions.dealName = "";
      this.quickActions.convertNote = "";
      this.quickActions.convertStageId =
        defaultLeadStageId || this.quickConvertStageOptions[0]?.id || parsePositiveId(this.record?.stage_id) || null;
      this.quickActions.convertOwnerUserId = defaultOwnerUserId || parsePositiveId(this.record?.owner_user_id) || null;
      this.quickActions.dealPipelineId = defaultPipelineId || this.selectedDealPipelineId;
      this.quickActions.dealStageId = defaultDealStageId;
      this.ensureConvertDealSelection();
      this.dialogs.convert = true;
    },
    onConvertPipelineChanged(value) {
      this.quickActions.dealPipelineId = parsePositiveId(value) || null;
      this.ensureConvertDealSelection();
    },
    ensureConvertDealSelection() {
      const selectedPipelineId = parsePositiveId(this.quickActions.dealPipelineId || this.selectedDealPipelineId);
      if (selectedPipelineId !== parsePositiveId(this.quickActions.dealPipelineId)) {
        this.quickActions.dealPipelineId = selectedPipelineId;
      }
      const stageOptions = this.quickDealStageOptions;
      const currentStageId = parsePositiveId(this.quickActions.dealStageId);
      if (!currentStageId || !stageOptions.some((entry) => entry.id === currentStageId)) {
        this.quickActions.dealStageId = stageOptions[0]?.id || null;
      }
    },
    async saveStageAction() {
      if (!this.leadId || !this.quickActions.stageId) return;
      const fallbackOwnerId = parsePositiveId(this.record?.owner_user_id);
      const fallbackOwnerLabel = this.ownerLabel(this.record || {});
      const targetStage = this.quickStageOptions.find((option) => option.id === this.quickActions.stageId) || null;
      const taskClosureRecord = {
        ...(this.record || {}),
        open_activity_count: this.openLeadActivitiesCount,
      };
      const closeOpenActivities = resolveLeadTerminalTaskClosureConfirmation({
        stage: targetStage,
        record: taskClosureRecord,
        translate: this.$t,
        entityLabel: this.recordTitle,
      });
      if (closeOpenActivities === null) return;
      this.quickActionLoading = true;
      try {
        const payload = {
          stage_id: this.quickActions.stageId,
        };
        if (closeOpenActivities) payload.close_open_activities = true;
        const { data } = await axios.patch(`/ncrm/leads/entries/${this.leadId}`, payload);
        this.menus.stage = false;
        this.historyLoaded = false;
        await this.fetchRecord();
        const convertedDealId = parsePositiveId(data?.conversion?.deal_id);
        if (convertedDealId) {
          this.showSnackbar(
            this.$t("ncrm.leads.details.messages.autoConverted", { id: convertedDealId }),
            "success"
          );
        } else {
          this.showSnackbar(this.$t("ncrm.leads.details.messages.stageUpdated"), "success");
        }
        await this.maybeOpenOwnerClaimDialog({
          ownerUserId: parsePositiveId(data?.entry?.owner_user_id) || parsePositiveId(this.record?.owner_user_id) || fallbackOwnerId,
          ownerLabel: normalizeNcrmLeadString(data?.entry?.owner_name) || this.ownerLabel(this.record || {}) || fallbackOwnerLabel,
        });
      } catch (error) {
        if (isAxiosRequestAborted(error)) return;
        console.error("[ncrm-lead-details] stage quick action error:", error);
        const responseCode = normalizeNcrmLeadString(error?.response?.data?.code).toUpperCase();
        const conversionDefaults = error?.response?.data?.conversion_defaults || {};
        if (responseCode === "AUTO_CONVERT_REQUIRES_INPUT") {
          this.menus.stage = false;
          this.openConvertDialogWithDefaults(conversionDefaults);
          this.showSnackbar(this.$t("ncrm.leads.details.messages.autoConvertNeedsInput"), "warning");
        } else {
          this.showSnackbar(this.$t("ncrm.leads.details.messages.quickActionError"), "error");
        }
      } finally {
        this.quickActionLoading = false;
      }
    },
    async saveOwnerAction() {
      if (!this.leadId || !this.quickActions.ownerUserId) return;
      this.quickActionLoading = true;
      try {
        await axios.patch(`/ncrm/leads/entries/${this.leadId}`, {
          owner_user_id: this.quickActions.ownerUserId,
        });
        this.menus.owner = false;
        this.historyLoaded = false;
        await this.fetchRecord();
        this.showSnackbar(this.$t("ncrm.leads.details.messages.ownerUpdated"), "success");
      } catch (error) {
        if (isAxiosRequestAborted(error)) return;
        console.error("[ncrm-lead-details] owner quick action error:", error);
        this.showSnackbar(this.$t("ncrm.leads.details.messages.quickActionError"), "error");
      } finally {
        this.quickActionLoading = false;
      }
    },
    async saveConvertAction() {
      if (!this.leadId || !this.canConvertLead) return;
      const selectedOwnerUserId = parsePositiveId(this.quickActions.convertOwnerUserId);
      const fallbackOwnerId = selectedOwnerUserId || parsePositiveId(this.record?.owner_user_id);
      const fallbackOwnerLabel = selectedOwnerUserId
        ? this.ownerOptionLabel(selectedOwnerUserId)
        : this.ownerLabel(this.record || {});
      const dealPipelineId = parsePositiveId(this.quickActions.dealPipelineId || this.selectedDealPipelineId);
      const dealStageId = parsePositiveId(this.quickActions.dealStageId);
      if (!dealPipelineId || !dealStageId) {
        this.showSnackbar(this.$t("ncrm.leads.details.messages.convertDealTargetMissing"), "warning");
        return;
      }
      this.quickActionLoading = true;
      try {
        const payload = {
          deal_name: normalizeNcrmLeadString(this.quickActions.dealName) || undefined,
          deal_pipeline_id: dealPipelineId,
          deal_stage_id: dealStageId,
          note: normalizeNcrmLeadString(this.quickActions.convertNote) || undefined,
        };
        const stageId = parsePositiveId(this.quickActions.convertStageId);
        const ownerUserId = parsePositiveId(this.quickActions.convertOwnerUserId);
        if (stageId) payload.stage_id = stageId;
        if (ownerUserId) payload.owner_user_id = ownerUserId;
        const { data } = await axios.post(`/ncrm/leads/entries/${this.leadId}/convert`, payload);
        this.dialogs.convert = false;
        this.historyLoaded = false;
        await this.fetchRecord();
        this.showSnackbar(this.$t("ncrm.leads.details.messages.converted"), "success");
        await this.maybeOpenOwnerClaimDialog({
          ownerUserId: parsePositiveId(data?.entry?.owner_user_id) || parsePositiveId(this.record?.owner_user_id) || fallbackOwnerId,
          ownerLabel: normalizeNcrmLeadString(data?.entry?.owner_name) || this.ownerLabel(this.record || {}) || fallbackOwnerLabel,
        });
      } catch (error) {
        if (isAxiosRequestAborted(error)) return;
        console.error("[ncrm-lead-details] convert quick action error:", error);
        this.showSnackbar(this.$t("ncrm.leads.details.messages.convertError"), "error");
      } finally {
        this.quickActionLoading = false;
      }
    },
    async fetchRecord() {
      if (!this.leadId) {
        this.clearPendingStageChange();
        this.record = null;
        this.sourceRecord = null;
        this.board = null;
        this.boardStages = [];
        this.dealPipelines = [];
        this.owners = [];
        this.leadActivities = [];
        this.leadActivitiesLoaded = false;
        this.propertyLayoutDrawer = false;
        if (typeof this.resetPropertyLayoutState === "function") {
          this.resetPropertyLayoutState();
        } else {
          this.propertyLayoutLoaded = false;
          this.propertyLayoutAvailableKeys = [];
          this.propertyLayoutVisibleKeys = [];
        }
        return;
      }
      this.loading = true;
      try {
        const { data } = await axios.get(`/ncrm/leads/entries/${this.leadId}`);
        this.record = data?.entry || null;
        this.board = data?.board || null;
        this.leadActivities = [];
        this.leadActivitiesLoaded = false;
        await Promise.all([this.fetchSourceRecord({ silent: true }), this.fetchQuickReferences({ silent: true })]);
        await this.fetchPropertyLayout({ force: true, silent: true });
        if (this.mainTab === "history") {
          await this.fetchHistory({ force: true });
        }
      } catch (error) {
        if (isAxiosRequestAborted(error)) return;
        console.error("[ncrm-lead-details] load error:", error);
        this.clearPendingStageChange();
        this.record = null;
        this.board = null;
        this.boardStages = [];
        this.dealPipelines = [];
        this.owners = [];
        this.leadActivities = [];
        this.leadActivitiesLoaded = false;
        this.historyItems = [];
        this.historyLoaded = false;
        this.sourceRecord = null;
        this.propertyLayoutDrawer = false;
        if (typeof this.resetPropertyLayoutState === "function") {
          this.resetPropertyLayoutState();
        } else {
          this.propertyLayoutLoaded = false;
          this.propertyLayoutAvailableKeys = [];
          this.propertyLayoutVisibleKeys = [];
        }
        this.showSnackbar(this.$t("ncrm.leads.details.messages.loadError"), "error");
      } finally {
        this.loading = false;
      }
    },
    async fetchHistory({ force = false } = {}) {
      if (!this.leadId) return;
      if (this.historyLoading) return;
      if (!force && this.historyLoaded) return;
      this.historyLoading = true;
      try {
        const { data } = await axios.get(`/ncrm/leads/entries/${this.leadId}/history`);
        this.historyItems = Array.isArray(data?.items) ? data.items : [];
        this.historyLoaded = true;
      } catch (error) {
        if (isAxiosRequestAborted(error)) return;
        console.error("[ncrm-lead-details] history load error:", error);
        this.showSnackbar(this.$t("ncrm.leads.details.messages.loadHistoryError"), "error");
      } finally {
        this.historyLoading = false;
      }
    },
    goBackToLeads() {
      const hasHistory = typeof window !== "undefined" && Number(window?.history?.length || 0) > 1;
      if (hasHistory && typeof this.$router?.back === "function") {
        this.$router.back();
        return;
      }
      const queryBoardId = parsePositiveId(this.$route?.query?.boardId);
      const boardId = queryBoardId || parsePositiveId(this.board?.id || this.record?.board_id);
      if (boardId) {
        this.$router.push({ name: "NcrmLeadBoard", params: { id: String(boardId) } }).catch(() => {});
        return;
      }
      this.$router.push({ name: "NcrmLeadsHome" }).catch(() => {});
    },
    openBoard() {
      const boardId = parsePositiveId(this.board?.id || this.record?.board_id);
      if (!boardId) return;
      this.$router
        .push({
          name: "NcrmLeadBoard",
          params: { id: String(boardId) },
          query: { view: "board" },
        })
        .catch(() => {});
    },
    openSourceRecord() {
      if (!this.sourceRoute) return;
      this.$router.push(this.sourceRoute).catch(() => {});
    },
  },
};
</script>
<style scoped>
.ncrm-record-title {
  color: var(--ncrm-color-text-strong);
}
.ncrm-record-header__kpis {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.ncrm-record-header__kpi {
  appearance: none;
  border: 1px solid var(--ncrm-color-border-strong);
  border-radius: var(--app-radius-base);
  padding: 8px 10px;
  min-width: 176px;
  background-color: var(--ncrm-color-surface-muted);
  color: inherit;
  font: inherit;
  text-align: left;
}
.ncrm-record-header__kpi--clickable {
  cursor: pointer;
  transition: background-color 0.2s ease, border-color 0.2s ease;
}
.ncrm-record-header__kpi--clickable:hover {
  background-color: var(--ncrm-color-hover-default);
  border-color: var(--ncrm-color-primary-border);
}
.ncrm-record-header__kpi--clickable:focus-visible {
  outline: 2px solid var(--ncrm-color-primary-focus);
  outline-offset: 2px;
}
.ncrm-record-header__kpi--clickable:disabled {
  cursor: default;
  opacity: 0.8;
}
.ncrm-record-header__kpi-value {
  color: var(--ncrm-color-text-strong);
  font-weight: 600;
}
.ncrm-lead-details__kpi-menu-list {
  min-width: 260px;
}
.ncrm-details-list {
  display: grid;
  gap: 6px;
}
.ncrm-details-list__item {
  border-bottom: 1px solid var(--ncrm-color-border-default);
  padding-bottom: 4px;
}
.ncrm-details-list__item:last-child {
  border-bottom: none;
  padding-bottom: 0;
}
.ncrm-details-list__value {
  color: var(--ncrm-color-text-strong);
  word-break: break-word;
}
.ncrm-main-workspace-tabs {
  border-bottom: 1px solid var(--ncrm-color-border-default);
}
.ncrm-main-workspace-window {
  padding-top: 14px;
}
.ncrm-lead-details__history-list :deep(.v-list-item-subtitle) {
  white-space: normal;
}
.ncrm-lead-details__history-list :deep(.v-list-subheader) {
  min-height: 28px;
  padding-left: 0;
}
.ncrm-lead-details__history-filter {
  max-width: 320px;
}
.ncrm-lead-details__history-filter-wrap {
  width: min(100%, 320px);
}
.ncrm-lead-details__history-filter-label {
  line-height: 1.2;
  margin-bottom: 4px;
}
.ncrm-lead-details__history-toolbar {
  padding-top: 4px;
  overflow: visible;
}
.ncrm-lead-details__next-action-alert {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 12px;
  border-radius: var(--app-radius-base);
  border: 1px solid var(--ncrm-color-context-info-border);
  background: var(--ncrm-color-context-info-bg);
  color: var(--ncrm-color-context-info-text);
}
.ncrm-lead-details__next-action-alert--success {
  border-color: var(--ncrm-color-context-success-border);
  background: var(--ncrm-color-context-success-bg);
  color: var(--ncrm-color-context-success-text);
}
.ncrm-lead-details__next-action-alert--warning {
  border-color: var(--ncrm-color-context-warning-border);
  background: var(--ncrm-color-context-warning-bg);
  color: var(--ncrm-color-context-warning-text);
}
.ncrm-lead-details__next-action-alert--error {
  border-color: var(--ncrm-color-context-error-border);
  background: var(--ncrm-color-context-error-bg);
  color: var(--ncrm-color-context-error-text);
}
.ncrm-lead-details__next-action-icon {
  margin-top: 1px;
}
</style>
